import { useTranslation } from "react-i18next";

import type { Branch, Contact } from "@/api/queries";
import { useCompanyContacts, useSetContactDisabled } from "@/api/queries";
import {
  Button,
  CreatedBy,
  EmptyState,
  ErrorState,
  Spinner,
  Tag,
} from "@/components/ui";
import { ContactRecordingTag } from "@/components/CompanyTags";
import { ContactReach } from "./ContactReach";

/** Two initials at most — "HR", never "HRDLC". */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * One person at the company, as screen 04 draws them (.docs/08 §7 F3): who they
 * are, where they sit, and the ways to reach them from here.
 *
 * What the card lets someone do to the record depends on who is looking. A
 * person's record is management's to change, at a seller's request — so a
 * manager gets *Editar* and *Deshabilitar*, and a seller gets the one thing a
 * seller may do on their own: add a number or an address. The server holds
 * the same line; this only stops the card offering what would be refused.
 *
 * A disabled contact stays on the list, dimmed and with nothing to reach them
 * by. Hidden, the next seller would add the same person again.
 */
export function ContactCard({
  contact,
  branches,
  companyId,
  canManage,
  onEdit,
  onAddChannels,
}: {
  contact: Contact;
  branches: Branch[];
  companyId: number;
  /** `MANAGER_ROLES` — who may change the record or switch it off. */
  canManage: boolean;
  onEdit: () => void;
  onAddChannels: () => void;
}) {
  const { t } = useTranslation();
  const toggle = useSetContactDisabled(companyId);
  const branch = branches.find((candidate) => candidate.id === contact.branch);
  const disabled = Boolean(contact.disabled_at);
  // Dimmed, not the whole card: the buttons that bring it back have to read.
  const dimmed = disabled ? "opacity-60" : "";

  return (
    <article className="border-2 border-rule bg-panel px-5 py-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className={`flex min-w-0 gap-3.5 ${dimmed}`}>
          <span
            aria-hidden="true"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center bg-mute-300 font-display text-[13px] font-extrabold"
          >
            {initials(contact.name)}
          </span>
          <div className="min-w-0">
            <p className="font-display text-[15px] font-extrabold">{contact.name}</p>
            <p className="text-xs text-mute-600">
              {[contact.job_title, branch?.name].filter(Boolean).join(" · ")}
            </p>
            <CreatedBy
              created={contact.created}
              by={contact.created_by_agent_name}
              className="mt-1"
            />
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {disabled ? (
            <Tag
              tone="outline"
              className="text-[10px] tracking-[0.06em] uppercase"
              title={t("companyDetail.contactDisabledHint")}
            >
              {t("companyDetail.contactDisabled")}
            </Tag>
          ) : contact.is_decision_maker ? (
            <Tag tone="accent" className="text-[10px] tracking-[0.06em] uppercase">
              {t("companyDetail.decisionMaker")}
            </Tag>
          ) : null}
          {/* Who at the company has heard it and what they answered, which the
              company's own tags cannot say: they are lit by any one of them. */}
          {disabled ? null : (
            <ContactRecordingTag answer={contact.consents_to_recording} />
          )}
          {canManage ? (
            <>
              <Button variant="ghost" className="px-2 py-1 text-xs" onClick={onEdit}>
                {t("companyDetail.edit")}
              </Button>
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ id: contact.id, disabled: !disabled })}
              >
                {disabled
                  ? t("companyDetail.enableContact")
                  : t("companyDetail.disableContact")}
              </Button>
            </>
          ) : disabled ? null : (
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              onClick={onAddChannels}
            >
              {t("companyDetail.addChannels")}
            </Button>
          )}
        </div>
      </div>

      {toggle.isError ? (
        <p role="alert" className="mt-3 text-xs text-brand-700">
          {toggle.error instanceof Error
            ? toggle.error.message
            : t("errors.saveContact")}
        </p>
      ) : null}

      <ContactReach contact={contact} />
    </article>
  );
}

/**
 * The people at the company, as cards. Its own component so the request is
 * made when the section opens and not before: screen 04 mounts this only
 * inside an open collapsible, and never for a colleague's account.
 */
export function CompanyContacts({
  companyId,
  branches,
  canManage,
  onEdit,
  onAddChannels,
}: {
  companyId: number;
  branches: Branch[];
  canManage: boolean;
  onEdit: (contact: Contact) => void;
  onAddChannels: (contact: Contact) => void;
}) {
  const { t } = useTranslation();
  const contacts = useCompanyContacts(companyId);
  // The ones still in use first. A stable sort, so the server's own order —
  // decision makers, then by name — holds inside each half.
  const rows = [...(contacts.data?.results ?? [])].sort(
    (a, b) => Number(Boolean(a.disabled_at)) - Number(Boolean(b.disabled_at)),
  );

  if (contacts.isPending) return <Spinner label={t("common.loading")} />;
  if (contacts.isError) {
    return (
      <ErrorState
        message={t("companies.contactsFailed")}
        onRetry={() => void contacts.refetch()}
      />
    );
  }
  if (rows.length === 0) {
    return <EmptyState>{t("companyDetail.noContacts")}</EmptyState>;
  }
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {rows.map((contact) => (
        <ContactCard
          key={contact.id}
          contact={contact}
          branches={branches}
          companyId={companyId}
          canManage={canManage}
          onEdit={() => onEdit(contact)}
          onAddChannels={() => onAddChannels(contact)}
        />
      ))}
    </div>
  );
}
